import {ChangeDetectionStrategy, Component, HostBinding, inject, OnInit, ViewChild} from '@angular/core';
import {map} from 'rxjs/operators';
import {FileState, InternalNotificationMessageCode} from '|api/commons';
import {FileAuthorizedOperation} from '|api/core';
import {
  CirculationToolbarButtonsComponent,
  DocumentView,
  EntityCommentsViewerComponent,
  FileDetailService,
  FileDtoWithAuthorization,
  getObjectTagColor,
  isAuthorizedOperationGranted,
  load,
  ObjectDetailPart,
  TaskActivityStateIconComponent,
} from '|shared';
import {FileDto} from '|api/document';
import {FileDetailRoute} from '../../enums/file-detail-routes.enum';
import {
  IconComponent,
  LabelComponent,
  LoadingIndicatorService,
  TagComponent,
  TooltipDirective
} from '@icz/angular-essentials';
import {FileToolbarButtonsComponent} from '../file-toolbar-buttons/file-toolbar-buttons.component';
import {AsyncPipe, NgClass} from '@angular/common';
import {
  DocumentFileDetailToolbarComponent
} from '../../../document-shared/components/document-file-detail-toolbar.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {WebSocketNotificationsService} from '|ui-essl-core';

@Component({
  selector: 'icz-file-detail-toolbar',
  templateUrl: './file-detail-toolbar.component.html',
  styleUrls: ['./file-detail-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TagComponent, IconComponent, TooltipDirective, TaskActivityStateIconComponent, LabelComponent, FileToolbarButtonsComponent, CirculationToolbarButtonsComponent, AsyncPipe, EntityCommentsViewerComponent, NgClass]
})
export class FileDetailToolbarComponent extends DocumentFileDetailToolbarComponent<FileDtoWithAuthorization> implements OnInit {
  protected loadingService = inject(LoadingIndicatorService);
  protected fileDetailService = inject(FileDetailService);
  private wsNotificationService = inject(WebSocketNotificationsService);

  @HostBinding('class.deleted')
  get isFileDeletedOrDeactivatedOrExported() {
    return this.isFileDeleted || this.isFileDeactivated || this.isFileExported;
  }

  @ViewChild(EntityCommentsViewerComponent)
  entityCommentsViewerComponent!: EntityCommentsViewerComponent;

  get isFileDeleted() {
    return this.object?.fileState === FileState.DELETED;
  }

  get isFileExported() {
    return this.object?.fileState === FileState.EXPORTED;
  }

  get isFileDeactivated() {
    return this.object?.fileState === FileState.DEACTIVATED;
  }

  get allowComments() {
    return [FileState.OPEN, FileState.SUSPENDED].includes(this.object.fileState!);
  }

  get isFileReadyForAgendaTransfer() {
    return this.object?.readyForAgendaTransfer && (this.object as FileDto)?.fileState !== FileState.EXPORTED;
  }

  get isFileLost() {
    return (this.object as FileDto)?.isLost;
  }

  readonly DocumentView = DocumentView;
  readonly getObjectTagColor = getObjectTagColor;

  file$ = this.fileDetailService.object$;

  canDisplayFileActivities$ = this.file$.pipe(
    map(file => isAuthorizedOperationGranted(file?.authorization, FileAuthorizedOperation.FILE_SHOW_ACTIVITIES)),
  );

  canDisplayFileProfile$ = this.file$.pipe(
    map(file => isAuthorizedOperationGranted(file?.authorization, FileAuthorizedOperation.FILE_SHOW_PROFILE)),
  );

  get showConfidentialIcon() {
    return false; // todo(lp) add display logic when FILE confidentiality implemented
  }

  override ngOnInit() {
    super.ngOnInit();
    this.wsNotificationService.getMessageListener$(InternalNotificationMessageCode.SETTLEMENT_FILE_SUCCESS)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
        this.entityCommentsViewerComponent.loadComments();
      }
    );
  }

  onCirculationActionCompleted() {
    this.fileDetailService.reloadObject({
      [ObjectDetailPart.OBJECT_DATA]: load(),
      [FileDetailRoute.ACTIVITIES]: load(),
    });
  }

  protected readonly FileState = FileState;
  protected readonly isAuthorizedOperationGranted = isAuthorizedOperationGranted;
  protected readonly FileAuthorizedOperation = FileAuthorizedOperation;

}
